/**
 * SoleadLab 2.0 - On-Map Overlay Crop / Delete Area
 *
 * When overlayEditMode is 'crop' or 'delete', this hook draws directly
 * on the map instead of opening a modal:
 *
 *  - CROP:   click-drag a rectangle on the map → keeps only that region
 *  - DELETE: click to add polygon points → erases that area (transparent)
 *
 * A floating toolbar (Apply / Cancel / Undo) is injected into the map container.
 * Geographic coordinates are converted to overlay image pixels via inverse
 * bilinear interpolation so the edit matches what the user sees on the map.
 */

import { useEffect, useRef } from 'react';
import { useMapStore, useProjectStore, useUIStore } from '../../stores';
import { resolveSignedUrl } from '../../services/signedAssetUrl';
import { uploadAsset } from '../../services/assetApi';
import { isPlanOverlay, quadUvToPageUv } from '../../utils/planBake';
import { overlayRasterOpacity } from '../../utils/overlayPaint';
import type { Overlay } from '../../stores/projectStore';

/**
 * Commit a crop/erase result for a PLAN overlay.
 *
 * Plans differ from image overlays in two ways that matter here:
 *
 *  1. The edit must be RECORDED, not just painted. A bake re-renders the whole
 *     sheet from the PDF, so an erase that exists only in the proxy's pixels
 *     comes back the moment the plan is baked. Recording it in page-fraction
 *     space lets the converter punch the same hole at full resolution — and
 *     keeps it reversible, since the PDF is never modified.
 *  2. The edited proxy must be PERSISTED immediately. The image-overlay path
 *     writes a blob: URL straight into the store and waits for "Save to
 *     Server", which for a plan would leave the saved bounds describing an
 *     uncropped sheet while the screen shows a cropped one — the same
 *     store-vs-database drift that produced misplaced tiles before.
 */
async function commitPlanEdit(
  overlay: Overlay,
  blob: Blob,
  bounds: Overlay['bounds'],
  bakePatch: Partial<NonNullable<Overlay['bake']>>,
  siteId: string,
): Promise<boolean> {
  try {
    const uploaded = await uploadAsset(
      blob, siteId, 'overlay', `${overlay.name.replace(/\.[^.]+$/, '')}_preview.png`);
    useProjectStore.getState().updateOverlay(overlay.id, {
      url: uploaded.url,
      bounds,
      bake: { ...overlay.bake!, ...bakePatch },
    });
    return true;
  } catch (err) {
    console.warn('[overlayEdit] could not persist the plan edit:', err);
    // The caller still applies the edit locally, so the user sees what they
    // drew — but the bake parameter is gone, and a bake would quietly undo it.
    // Say so: a silent fall-back here reintroduces exactly the confusion this
    // feature was built to remove.
    useUIStore.getState().addNotification({
      type: 'warning',
      title: 'Edit not saved to the server',
      message: `${overlay.name}: the change is showing, but it could not be recorded. `
        + 'Baking now would re-render the plan without it — retry the edit first.',
    });
    return false;
  }
}

// ─── Geo ↔ Pixel helpers ────────────────────────────────────────────────────

/** Shared Newton iteration for inverse bilinear interpolation */
function solveUV(px: number, py: number, corners: number[][]): [number, number] {
  let u = 0.5, v = 0.5;
  for (let iter = 0; iter < 20; iter++) {
    const a = corners[0], b = corners[1], c = corners[2], d = corners[3];
    const fx = (1 - u) * (1 - v) * a[0] + u * (1 - v) * b[0] + u * v * c[0] + (1 - u) * v * d[0] - px;
    const fy = (1 - u) * (1 - v) * a[1] + u * (1 - v) * b[1] + u * v * c[1] + (1 - u) * v * d[1] - py;
    const dfu = -(1 - v) * a[0] + (1 - v) * b[0] + v * c[0] - v * d[0];
    const dfv = -(1 - u) * a[0] - u * b[0] + u * c[0] + (1 - u) * d[0];
    const dgu = -(1 - v) * a[1] + (1 - v) * b[1] + v * c[1] - v * d[1];
    const dgv = -(1 - u) * a[1] - u * b[1] + u * c[1] + (1 - u) * d[1];
    const det = dfu * dgv - dfv * dgu;
    if (Math.abs(det) < 1e-15) break;
    const du = -(fx * dgv - fy * dfv) / det;
    const dv = -(dfu * fy - dgu * fx) / det;
    u += du; v += dv;
    if (Math.abs(du) < 1e-10 && Math.abs(dv) < 1e-10) break;
  }
  return [u, v];
}

/**
 * Inverse bilinear interpolation: given geo point P inside a quad defined by
 * corners TL(0), TR(1), BR(2), BL(3), return (u,v) clamped to [0,1]×[0,1].
 * Points drawn outside the overlay boundary are clamped to the nearest edge.
 */
function geoToUVClamped(
  px: number, py: number,
  corners: number[][]
): [number, number] {
  const [u, v] = solveUV(px, py, corners);
  return [Math.max(0, Math.min(1, u)), Math.max(0, Math.min(1, v))];
}

// ─── Floating toolbar factory ────────────────────────────────────────────────

function createToolbar(
  mode: 'crop' | 'delete',
  onApply: () => void,
  onCancel: () => void,
  onUndo?: () => void,
): HTMLDivElement {
  const bar = document.createElement('div');
  bar.className = 'overlay-edit-toolbar';
  bar.style.cssText =
    'position:absolute; top:60px; left:50%; transform:translateX(-50%); z-index:450; ' +
    'display:flex; gap:8px; padding:10px 16px; background:#ffffff; border:1px solid #e5e7eb; ' +
    'border-radius:12px; box-shadow:0 6px 20px rgba(0,0,0,0.06); align-items:center; font-family:Inter,sans-serif;';

  const label = document.createElement('span');
  label.style.cssText = 'color:#6b7280; font-size:13px; margin-right:6px;';
  label.textContent = mode === 'crop'
    ? 'Drag rectangle to crop'
    : 'Click to add points, then Apply';
  bar.appendChild(label);

  if (mode === 'delete' && onUndo) {
    const undoBtn = document.createElement('button');
    undoBtn.textContent = 'Undo';
    undoBtn.style.cssText = btnStyle('#ffffff', '#1c1f23', '#e5e7eb');
    undoBtn.onmouseenter = () => { undoBtn.style.background = '#f3f5f7'; };
    undoBtn.onmouseleave = () => { undoBtn.style.background = '#ffffff'; };
    undoBtn.onclick = onUndo;
    bar.appendChild(undoBtn);
  }

  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  cancelBtn.style.cssText = btnStyle('#ffffff', '#1c1f23', '#e5e7eb');
  cancelBtn.onmouseenter = () => { cancelBtn.style.background = '#f3f5f7'; };
  cancelBtn.onmouseleave = () => { cancelBtn.style.background = '#ffffff'; };
  cancelBtn.onclick = onCancel;
  bar.appendChild(cancelBtn);

  const applyBtn = document.createElement('button');
  applyBtn.textContent = 'Apply';
  applyBtn.style.cssText = btnStyle('#2f80ff', '#ffffff', '#2f80ff') + 'font-weight:600;';
  applyBtn.onmouseenter = () => { applyBtn.style.background = '#1a6fff'; };
  applyBtn.onmouseleave = () => { applyBtn.style.background = '#2f80ff'; };
  applyBtn.onclick = onApply;
  bar.appendChild(applyBtn);

  return bar;
}

function btnStyle(bg: string, color: string, border: string): string {
  return `padding:6px 14px; border-radius:6px; cursor:pointer; font-size:13px; background:${bg}; color:${color}; border:1px solid ${border};`;
}

// ─── Source/layer IDs ────────────────────────────────────────────────────────

const EDIT_SOURCE = 'overlay-edit-draw-source';
const EDIT_FILL_LAYER = 'overlay-edit-draw-fill';
const EDIT_LINE_LAYER = 'overlay-edit-draw-line';
const EDIT_POINTS_LAYER = 'overlay-edit-draw-points';

// ─── Hook ────────────────────────────────────────────────────────────────────

export default function useOverlayEdit() {
  const overlayEditMode = useMapStore((s) => s.overlayEditMode);
  const activeOverlayId = useMapStore((s) => s.activeOverlayId);
  const isMapReady = useMapStore((s) => s.isMapReady);

  // Refs to persist across renders without causing re-render
  const cropStartRef = useRef<[number, number] | null>(null);
  const cropEndRef = useRef<[number, number] | null>(null);
  const polyPointsRef = useRef<[number, number][]>([]);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const cropFrameRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);

  useEffect(() => {
    const map = useMapStore.getState().map;
    if (!map || !isMapReady) return;

    // ── Cleanup helper ───────────────────────────────────────────────────
    const cleanup = () => {
      if (toolbarRef.current) {
        try { toolbarRef.current.remove(); } catch (_e) { /* */ }
        toolbarRef.current = null;
      }
      if (cropFrameRef.current) {
        try { cropFrameRef.current.remove(); } catch (_e) { /* */ }
        cropFrameRef.current = null;
      }
      try { if (map.getLayer(EDIT_FILL_LAYER)) map.removeLayer(EDIT_FILL_LAYER); } catch (_e) { /* */ }
      try { if (map.getLayer(EDIT_LINE_LAYER)) map.removeLayer(EDIT_LINE_LAYER); } catch (_e) { /* */ }
      try { if (map.getLayer(EDIT_POINTS_LAYER)) map.removeLayer(EDIT_POINTS_LAYER); } catch (_e) { /* */ }
      try { if (map.getSource(EDIT_SOURCE)) map.removeSource(EDIT_SOURCE); } catch (_e) { /* */ }

      cropStartRef.current = null;
      cropEndRef.current = null;
      polyPointsRef.current = [];
      isDraggingRef.current = false;

      // Restore default cursor
      try { map.getCanvas().style.cursor = ''; } catch (_e) { /* map may be disposed */ }
    };

    cleanup();

    if (!overlayEditMode || !activeOverlayId) return cleanup;

    const overlay = useProjectStore.getState().data.overlays.find((o) => o.id === activeOverlayId);
    if (!overlay) return cleanup;

    const mode = overlayEditMode;

    // Set crosshair cursor
    map.getCanvas().style.cursor = 'crosshair';

    // Disable map drag during crop rectangle drawing
    const origDragPan = map.dragPan.isEnabled();

    // ── Drawing feedback ─────────────────────────────────────────────────
    if (mode === 'crop') {
      // Screen-anchored crop frame: a DOM rectangle, always LEVEL with the
      // viewport and deliberately NOT tracking the map (owner-specified
      // workflow). The user rotates / pans / zooms the map UNDER the frame —
      // right-drag rotates — until the wanted area sits level inside it;
      // Apply crops whatever is under the frame at that moment. A geojson
      // rectangle cannot do this: it is geo-anchored, so it tilts with the
      // map and, drawn north-up, can never hug a rotated sheet.
      const frame = document.createElement('div');
      frame.style.cssText =
        'position:absolute;pointer-events:none;z-index:440;display:none;' +
        'background:rgba(59,130,246,0.2);border:2px dashed #3b82f6;';
      map.getContainer().appendChild(frame);
      cropFrameRef.current = frame;
    } else {
      map.addSource(EDIT_SOURCE, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      // Red fill + dashed border + point circles for delete polygon
      map.addLayer({
        id: EDIT_FILL_LAYER,
        type: 'fill',
        source: EDIT_SOURCE,
        paint: { 'fill-color': '#ef4444', 'fill-opacity': 0.3 },
        filter: ['==', '$type', 'Polygon'],
      });
      map.addLayer({
        id: EDIT_LINE_LAYER,
        type: 'line',
        source: EDIT_SOURCE,
        paint: { 'line-color': '#ef4444', 'line-width': 2, 'line-dasharray': [5, 4] },
        filter: ['==', '$type', 'Polygon'],
      });
      map.addLayer({
        id: EDIT_POINTS_LAYER,
        type: 'circle',
        source: EDIT_SOURCE,
        paint: {
          'circle-radius': 5,
          'circle-color': '#ef4444',
          'circle-stroke-color': '#fff',
          'circle-stroke-width': 2,
        },
        filter: ['==', '$type', 'Point'],
      });
    }

    // ── Position the screen-anchored crop frame from the drag extents ────
    const updateCropFrame = () => {
      const el = cropFrameRef.current;
      const s = cropStartRef.current;
      const e = cropEndRef.current;
      if (!el || !s || !e) return;
      el.style.display = 'block';
      el.style.left = `${Math.min(s[0], e[0])}px`;
      el.style.top = `${Math.min(s[1], e[1])}px`;
      el.style.width = `${Math.abs(e[0] - s[0])}px`;
      el.style.height = `${Math.abs(e[1] - s[1])}px`;
    };

    // ── Update the GeoJSON source with current drawing (delete mode) ─────
    const updateDrawSource = () => {
      const source = map.getSource(EDIT_SOURCE) as mapboxgl.GeoJSONSource | undefined;
      if (!source) return;

      const features: GeoJSON.Feature[] = [];

      {
        const pts = polyPointsRef.current;
        if (pts.length >= 3) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[...pts, pts[0]].map((p) => [p[0], p[1]])],
            },
            properties: {},
          });
        } else if (pts.length === 2) {
          features.push({
            type: 'Feature',
            geometry: {
              type: 'Polygon',
              coordinates: [[pts[0], pts[1], pts[0]].map((p) => [p[0], p[1]])],
            },
            properties: {},
          });
        }
        // Point markers for each vertex
        for (const p of pts) {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [p[0], p[1]] },
            properties: {},
          });
        }
      }

      source.setData({ type: 'FeatureCollection', features });
    };

    // ── Apply: perform the image operation ───────────────────────────────
    const applyEdit = async () => {
      const currentOverlay = useProjectStore.getState().data.overlays.find((o) => o.id === activeOverlayId);
      if (!currentOverlay) return;

      const bounds = currentOverlay.bounds;

      // Load the overlay image. crossOrigin='anonymous' is required so the
      // canvas (used below for crop/erase + toBlob) isn't tainted, but it
      // strips the sl_refresh cookie — pre-sign /api/v2/assets/ URLs.
      const signedUrl = await resolveSignedUrl(currentOverlay.url);
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.crossOrigin = 'anonymous';
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = signedUrl;
      });

      const W = img.width;
      const H = img.height;

      if (mode === 'crop') {
        const s = cropStartRef.current;
        const e = cropEndRef.current;
        if (!s || !e) return;

        // s/e are SCREEN pixels: the frame is viewport-anchored and the user
        // has adjusted the map under it, so unproject its 4 corners NOW —
        // what sits under the frame at Apply time is what gets cropped —
        // then take the UV bounding box (clamped, so a frame reaching past
        // the sheet crops from the edge). With the map rotated so the sheet
        // sits level on screen, the box hugs the sheet's own axes.
        const x0 = Math.min(s[0], e[0]), x1 = Math.max(s[0], e[0]);
        const y0 = Math.min(s[1], e[1]), y1 = Math.max(s[1], e[1]);
        const uvs = ([[x0, y0], [x1, y0], [x1, y1], [x0, y1]] as Array<[number, number]>)
          .map((p) => {
            const g = map.unproject(p);
            return geoToUVClamped(g.lng, g.lat, bounds);
          });

        // Pixel bounding box from UV coords
        const pxCoords = uvs.map((uv) => [uv[0] * W, uv[1] * H]);
        const minPx = Math.max(0, Math.floor(Math.min(...pxCoords.map((p) => p[0]))));
        const maxPx = Math.min(W, Math.ceil(Math.max(...pxCoords.map((p) => p[0]))));
        const minPy = Math.max(0, Math.floor(Math.min(...pxCoords.map((p) => p[1]))));
        const maxPy = Math.min(H, Math.ceil(Math.max(...pxCoords.map((p) => p[1]))));
        const cropW = maxPx - minPx;
        const cropH = maxPy - minPy;

        if (cropW < 5 || cropH < 5) {
          alert('Crop selection too small.');
          return;
        }

        // Crop the image
        const resultCanvas = document.createElement('canvas');
        resultCanvas.width = cropW;
        resultCanvas.height = cropH;
        const ctx = resultCanvas.getContext('2d')!;
        ctx.drawImage(img, minPx, minPy, cropW, cropH, 0, 0, cropW, cropH);

        // Compute new geo bounds for the cropped portion
        const uLeft = minPx / W, uRight = maxPx / W;
        const vTop = minPy / H, vBottom = maxPy / H;
        const lerp = (a: number[], b: number[], c: number[], d: number[], u: number, v: number): [number, number] => [
          (1 - u) * (1 - v) * a[0] + u * (1 - v) * b[0] + u * v * c[0] + (1 - u) * v * d[0],
          (1 - u) * (1 - v) * a[1] + u * (1 - v) * b[1] + u * v * c[1] + (1 - u) * v * d[1],
        ];
        const newBounds: [[number, number], [number, number], [number, number], [number, number]] = [
          lerp(bounds[0], bounds[1], bounds[2], bounds[3], uLeft, vTop),
          lerp(bounds[0], bounds[1], bounds[2], bounds[3], uRight, vTop),
          lerp(bounds[0], bounds[1], bounds[2], bounds[3], uRight, vBottom),
          lerp(bounds[0], bounds[1], bounds[2], bounds[3], uLeft, vBottom),
        ];

        const blob = await new Promise<Blob | null>((resolve) => resultCanvas.toBlob(resolve, 'image/png'));
        if (!blob) return;

        // A plan records the crop as a bake parameter, composed into
        // page-fraction space so cropping an already-cropped sheet still
        // describes the right region of the PDF.
        const siteId = useProjectStore.getState().meta.siteId;
        if (isPlanOverlay(currentOverlay) && currentOverlay.bake && siteId) {
          const [pu0, pv0] = quadUvToPageUv(uLeft, vTop, currentOverlay.bake);
          const [pu1, pv1] = quadUvToPageUv(uRight, vBottom, currentOverlay.bake);
          const ok = await commitPlanEdit(
            currentOverlay, blob, newBounds,
            { cropRect: { u0: pu0, v0: pv0, u1: pu1, v1: pv1 } }, siteId,
          );
          if (ok) {
            const stored = useProjectStore.getState().data.overlays
              .find((o) => o.id === activeOverlayId);
            if (stored) {
              // Same rule as the image load above: Mapbox image sources fetch
              // with crossOrigin='anonymous' (no cookie), so an unsigned
              // /api/v2/assets URL 401s in production builds — pre-sign it.
              const signedStored = await resolveSignedUrl(stored.url);
              forceReloadMapSource(map, activeOverlayId, signedStored, newBounds,
                currentOverlay.opacity, currentOverlay.visible);
            }
            if (currentOverlay.url.startsWith('blob:')) URL.revokeObjectURL(currentOverlay.url);
            useMapStore.getState().setOverlayEditMode(null);
            return;
          }
          // fall through to the local-only path so the edit is not simply lost
        }

        if (currentOverlay.url.startsWith('blob:')) URL.revokeObjectURL(currentOverlay.url);
        const newUrl = URL.createObjectURL(blob);

        // Write the blob URL directly to Zustand (do NOT route through
        // updateOverlay — that PATCHes the entity API, which would persist
        // a `blob:` URL into D1 that can't survive a refresh-without-save).
        // The blob is uploaded to R2 + PATCHed with the real URL by
        // projectSync on the next Save, same as fresh-import overlays.
        useProjectStore.setState((state) => ({
          data: {
            ...state.data,
            overlays: state.data.overlays.map((o) =>
              o.id === activeOverlayId ? { ...o, url: newUrl, bounds: newBounds } : o
            ),
          },
          meta: { ...state.meta, isModified: true },
        }));
        forceReloadMapSource(map, activeOverlayId, newUrl, newBounds, currentOverlay.opacity, currentOverlay.visible);
      } else {
        // Delete polygon area
        const pts = polyPointsRef.current;
        if (pts.length < 3) {
          alert('Need at least 3 points.');
          return;
        }

        // Convert polygon vertices to pixel coords.
        // Points outside the overlay are clamped to the nearest edge so the
        // user can freely draw beyond the layer boundary.
        const pixelPts = pts.map((p) => {
          const uv = geoToUVClamped(p[0], p[1], bounds);
          return [uv[0] * W, uv[1] * H] as [number, number];
        });

        const resultCanvas = document.createElement('canvas');
        resultCanvas.width = W;
        resultCanvas.height = H;
        const ctx = resultCanvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0);

        // Clip + clear the polygon
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pixelPts[0][0], pixelPts[0][1]);
        for (let i = 1; i < pixelPts.length; i++) {
          ctx.lineTo(pixelPts[i][0], pixelPts[i][1]);
        }
        ctx.closePath();
        ctx.clip();
        ctx.clearRect(0, 0, W, H);
        ctx.restore();

        const blob = await new Promise<Blob | null>((resolve) => resultCanvas.toBlob(resolve, 'image/png'));
        if (!blob) return;

        // A plan APPENDS the erased shape to its bake parameters, in
        // page-fraction space, so the converter punches the same hole at full
        // resolution instead of the erase reappearing on the next bake.
        const siteIdErase = useProjectStore.getState().meta.siteId;
        if (isPlanOverlay(currentOverlay) && currentOverlay.bake && siteIdErase) {
          const pagePoly = pts.map((p) => {
            const uv = geoToUVClamped(p[0], p[1], bounds);
            return quadUvToPageUv(uv[0], uv[1], currentOverlay.bake);
          });
          const ok = await commitPlanEdit(
            currentOverlay, blob, bounds,
            { erasePolygons: [...(currentOverlay.bake.erasePolygons ?? []), pagePoly] },
            siteIdErase,
          );
          if (ok) {
            const stored = useProjectStore.getState().data.overlays
              .find((o) => o.id === activeOverlayId);
            if (stored) {
              // See crop branch: unsigned asset URLs 401 under Mapbox's
              // anonymous fetch in production — pre-sign.
              const signedStored = await resolveSignedUrl(stored.url);
              forceReloadMapSource(map, activeOverlayId, signedStored, bounds,
                currentOverlay.opacity, currentOverlay.visible);
            }
            if (currentOverlay.url.startsWith('blob:')) URL.revokeObjectURL(currentOverlay.url);
            useMapStore.getState().setOverlayEditMode(null);
            return;
          }
          // fall through to the local-only path so the edit is not simply lost
        }

        if (currentOverlay.url.startsWith('blob:')) URL.revokeObjectURL(currentOverlay.url);
        const newUrl = URL.createObjectURL(blob);

        // See crop branch above — direct setState avoids PATCHing a blob:
        // URL into the entity row.
        useProjectStore.setState((state) => ({
          data: {
            ...state.data,
            overlays: state.data.overlays.map((o) =>
              o.id === activeOverlayId ? { ...o, url: newUrl } : o
            ),
          },
          meta: { ...state.meta, isModified: true },
        }));
        forceReloadMapSource(map, activeOverlayId, newUrl, bounds, currentOverlay.opacity, currentOverlay.visible);
      }

      useMapStore.getState().setOverlayEditMode(null);
    };

    const cancelEdit = () => {
      useMapStore.getState().setOverlayEditMode(null);
    };

    const undoPoint = () => {
      polyPointsRef.current = polyPointsRef.current.slice(0, -1);
      updateDrawSource();
    };

    // ── Inject floating toolbar ──────────────────────────────────────────
    const container = map.getContainer();
    const toolbar = createToolbar(
      mode,
      applyEdit,
      cancelEdit,
      mode === 'delete' ? undoPoint : undefined
    );
    container.appendChild(toolbar);
    toolbarRef.current = toolbar;

    // ── Map event handlers ───────────────────────────────────────────────
    const handleMouseDown = (e: mapboxgl.MapMouseEvent) => {
      if (mode !== 'crop') return;
      // LEFT button only. Mapbox fires map 'mousedown' for every button, and
      // right-drag is the ROTATE gesture this workflow depends on (align the
      // sheet under the frame) — it must not reset the drawn frame.
      if (e.originalEvent.button !== 0) return;
      isDraggingRef.current = true;
      // SCREEN pixels, not lngLat: the frame is anchored to the viewport and
      // must stay level and stationary while the map moves beneath it.
      cropStartRef.current = [e.point.x, e.point.y];
      cropEndRef.current = [e.point.x, e.point.y];
      map.dragPan.disable();
    };

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (mode !== 'crop' || !isDraggingRef.current) return;
      cropEndRef.current = [e.point.x, e.point.y];
      updateCropFrame();
    };

    const handleMouseUp = () => {
      if (mode !== 'crop') return;
      isDraggingRef.current = false;
      if (origDragPan) map.dragPan.enable();
    };

    const handleClick = (e: mapboxgl.MapMouseEvent) => {
      if (mode !== 'delete') return;
      polyPointsRef.current = [...polyPointsRef.current, [e.lngLat.lng, e.lngLat.lat]];
      updateDrawSource();
    };

    // Keyboard: Escape to cancel, Enter to apply
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelEdit();
      if (e.key === 'Enter') applyEdit();
    };

    map.on('mousedown', handleMouseDown);
    map.on('mousemove', handleMouseMove);
    map.on('mouseup', handleMouseUp);
    if (mode === 'delete') {
      map.on('click', handleClick);
    }
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      map.off('mousedown', handleMouseDown);
      map.off('mousemove', handleMouseMove);
      map.off('mouseup', handleMouseUp);
      map.off('click', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
      if (origDragPan) map.dragPan.enable();
      cleanup();
    };
  }, [overlayEditMode, activeOverlayId, isMapReady]);
}

// ── Helper: force-reload a map image source ──────────────────────────────────

function forceReloadMapSource(
  map: mapboxgl.Map,
  overlayId: string,
  url: string,
  bounds: number[][],
  opacity: number,
  visible: boolean
) {
  const sourceId = `overlay-${overlayId}`;
  const layerId = `overlay-layer-${overlayId}`;

  try { if (map.getLayer(layerId)) map.removeLayer(layerId); } catch (_e) { /* */ }
  try { if (map.getSource(sourceId)) map.removeSource(sourceId); } catch (_e) { /* */ }

  map.addSource(sourceId, {
    type: 'image',
    url,
    coordinates: bounds as any,
  });

  map.addLayer({
    id: layerId,
    type: 'raster',
    source: sourceId,
    paint: {
      // Same zoom fade as the main overlay effect. This re-adds the layer after
      // every drag, rotate, crop and erase commit, so a plain number here would
      // read as "the fade breaks whenever I move a plan".
      'raster-opacity': overlayRasterOpacity({ visible, opacity }) as any,
      'raster-fade-duration': 0,
    },
    layout: {
      visibility: visible ? 'visible' : 'none',
    },
  });
}
